import { boolean, integer, pgTable, uniqueIndex } from 'drizzle-orm/pg-core'
import {
  getDateField,
  getId,
  getIdReference,
  getMonetaryField,
  getTimestampWithTimezone,
} from '../common/utils'
import { users } from './user'

// What the capacity heatmap grid looked like on a given day. The booking tables
// can't answer "how did this look a week ago": reassigning a technician
// hard-deletes the attendee row and rescheduling rewrites its time range in
// place, so the earlier state leaves no trace. The only way to compare against
// the past is to write it down as it happens.
//
// Dense — a row per employee per day, zeros included — so that the absence of
// rows for an employee means they weren't employed on that run, which is what
// lets the delta read "–" for a new hire instead of a fabricated gain.
export const capacitySnapshots = pgTable(
  'capacity_snapshots',
  {
    id: getId(),
    createdAt: getTimestampWithTimezone().notNull().defaultNow(),
    // The run this row belongs to (the "as of" key) and the instant the grid was
    // actually read — they differ for rows backfilled from an older dump.
    snapshotDate: getDateField().notNull(),
    capturedAt: getTimestampWithTimezone().notNull(),
    userId: getIdReference(() => users.id),
    day: getDateField().notNull(),
    // Inputs to the empty-day metric rather than the metric itself, so changing
    // what counts as a fillable day doesn't invalidate the stored history.
    totalMinutes: integer().notNull(),
    visitCount: integer().notNull(),
    blocked: boolean().notNull(),
  },
  (table) => [
    uniqueIndex('capacity_snapshots_run_user_day_idx').on(
      table.snapshotDate,
      table.userId,
      table.day,
    ),
  ],
)

// One row per day for the scalar KPIs whose inputs mutate underneath them
// (visit tasks get repriced and added, so a past backlog value can't be
// recomputed from today's rows).
//
// Typed columns rather than a generic metric/value table: the column list is
// the record of what we keep, the values stay money- and count-typed all the
// way to the frontend, and a metric added later starts NULL — which renders as
// "no data" instead of a zero that looks like a real measurement.
export const kpiDailySnapshots = pgTable('kpi_daily_snapshots', {
  id: getId(),
  createdAt: getTimestampWithTimezone().notNull().defaultNow(),
  snapshotDate: getDateField().notNull().unique(),
  capturedAt: getTimestampWithTimezone().notNull(),
  // Booked future visits and their net (VAT 0%) value at capture time.
  orderBacklogVisitCount: integer(),
  orderBacklogRevenue: getMonetaryField(),
})
